import type { AppUser } from "@/types/user";
import type { Organization } from "@/types/organization";
import type { Wedding } from "@/types/wedding";
import type { Guest } from "@/types/guest";
import type { BudgetItem } from "@/types/budget";
import type { ChecklistItem } from "@/types/checklist";
import type { TimelineMilestone } from "@/types/timeline";
import type { Vendor, VendorBooking } from "@/types/vendor";
import type { RunOfShowItem } from "@/types/run-of-show";
import type { Contract } from "@/types/contract";
import type { MoodboardItem, PaletteColor } from "@/types/moodboard";

/**
 * Dummy authentication fixtures.
 *
 * These stand in for real Supabase Auth + Postgres rows so the app shell,
 * routing, and context layer can be built, reviewed, and demoed before a
 * Supabase project exists. Every shape mirrors the real domain types
 * exactly, so swapping AuthContext's dummy provider for a Supabase-backed
 * one later is a implementation-only change — no consumer code changes.
 */

export const DUMMY_USER: AppUser = {
  id: "usr_01HZX0000000000000000001",
  email: "alex@atelier-suite.com",
  fullName: "Alex Rivera",
  createdAt: "2024-01-15T09:00:00.000Z",
};

export const DUMMY_ORGANIZATIONS: Organization[] = [
  {
    id: "org_01HZX0000000000000000001",
    name: "Rivera & Co. Weddings",
    slug: "rivera-co",
    plan: "studio",
    subscriptionStatus: "trialing",
    createdAt: "2024-01-15T09:00:00.000Z",
    weddingCount: 3,
    memberCount: 5,
  },
  {
    id: "org_01HZX0000000000000000002",
    name: "Maison Blanc Events",
    slug: "maison-blanc",
    plan: "agency",
    subscriptionStatus: "active",
    createdAt: "2023-11-02T09:00:00.000Z",
    weddingCount: 12,
    memberCount: 18,
  },
];

export const DUMMY_WEDDINGS: Wedding[] = [
  {
    id: "wed_01HZX0000000000000000001",
    organizationId: "org_01HZX0000000000000000001",
    coupleNames: "Sofía & Marco",
    weddingDate: "2026-10-17",
    venueName: "Hacienda Los Laureles",
    venueCity: "Guadalajara, Jalisco",
    stage: "planning",
    guestCountEstimate: 140,
    budgetTotal: 850000,
    createdAt: "2026-02-01T09:00:00.000Z",
  },
  {
    id: "wed_01HZX0000000000000000002",
    organizationId: "org_01HZX0000000000000000001",
    coupleNames: "Elena & Daniel",
    weddingDate: "2026-08-22",
    venueName: "Casa Luna Jardín",
    venueCity: "Tlaquepaque, Jalisco",
    stage: "final_month",
    guestCountEstimate: 90,
    budgetTotal: 520000,
    createdAt: "2025-11-20T09:00:00.000Z",
  },
  {
    id: "wed_01HZX0000000000000000003",
    organizationId: "org_01HZX0000000000000000001",
    coupleNames: "Valentina & Diego",
    weddingDate: "2027-02-14",
    stage: "inquiry",
    guestCountEstimate: 60,
    createdAt: "2026-06-10T09:00:00.000Z",
  },
];

export const DUMMY_GUESTS: Guest[] = [
  {
    id: "gst_01",
    weddingId: "wed_01HZX0000000000000000001",
    fullName: "Renata Solís",
    email: "renata.solis@example.com",
    phone: "+52 33 1234 5678",
    side: "partner1",
    group: "Family",
    partySize: 2,
    rsvpStatus: "confirmed",
    dietaryNotes: "Vegetarian",
    createdAt: "2026-02-03T09:00:00.000Z",
    invitationStatus: "sent",
    invitationChannel: "whatsapp",
    invitedAt: "2026-03-01T10:00:00.000Z",
    lastContactedAt: "2026-03-01T10:00:00.000Z",
    respondedAt: "2026-03-04T18:20:00.000Z",
    attendingCount: 2,
    responseNote: "¡No nos la perdemos!",
  },
  {
    id: "gst_02",
    weddingId: "wed_01HZX0000000000000000001",
    fullName: "Emilio Castañeda",
    email: "emilio.c@example.com",
    side: "partner2",
    group: "Wedding party",
    partySize: 1,
    rsvpStatus: "confirmed",
    createdAt: "2026-02-03T09:05:00.000Z",
    invitationStatus: "sent",
    invitationChannel: "email",
    invitedAt: "2026-03-01T10:05:00.000Z",
    lastContactedAt: "2026-03-01T10:05:00.000Z",
    respondedAt: "2026-03-02T09:15:00.000Z",
    attendingCount: 1,
  },
  {
    id: "gst_03",
    weddingId: "wed_01HZX0000000000000000001",
    fullName: "Familia Ortega Ramírez",
    phone: "+52 33 9876 5432",
    side: "partner1",
    group: "Family",
    partySize: 4,
    rsvpStatus: "pending",
    createdAt: "2026-02-04T10:00:00.000Z",
    invitationStatus: "reminded",
    invitationChannel: "whatsapp",
    invitedAt: "2026-03-01T11:00:00.000Z",
    lastContactedAt: "2026-04-10T09:00:00.000Z",
  },
  {
    id: "gst_04",
    weddingId: "wed_01HZX0000000000000000001",
    fullName: "Daniela Preciado",
    email: "daniela.preciado@example.com",
    side: "partner2",
    group: "Friends",
    partySize: 2,
    rsvpStatus: "declined",
    notes: "Traveling abroad that week",
    createdAt: "2026-02-05T11:30:00.000Z",
    invitationStatus: "sent",
    invitationChannel: "email",
    invitedAt: "2026-03-02T09:00:00.000Z",
    lastContactedAt: "2026-03-02T09:00:00.000Z",
    respondedAt: "2026-03-06T14:00:00.000Z",
    attendingCount: 0,
    responseNote: "Estaremos fuera del país, ¡felicidades!",
  },
  {
    id: "gst_05",
    weddingId: "wed_01HZX0000000000000000001",
    fullName: "Grupo de trabajo — Contabilidad",
    side: "shared",
    group: "Work",
    partySize: 3,
    rsvpStatus: "pending",
    createdAt: "2026-02-06T09:00:00.000Z",
    invitationStatus: "not_sent",
  },
  {
    id: "gst_06",
    weddingId: "wed_01HZX0000000000000000002",
    fullName: "Paulina Vázquez",
    email: "paulina.vazquez@example.com",
    side: "partner1",
    group: "Friends",
    partySize: 1,
    rsvpStatus: "confirmed",
    createdAt: "2025-12-01T09:00:00.000Z",
    invitationStatus: "sent",
    invitationChannel: "email",
    invitedAt: "2025-12-15T09:00:00.000Z",
    lastContactedAt: "2025-12-15T09:00:00.000Z",
    respondedAt: "2025-12-18T09:00:00.000Z",
    attendingCount: 1,
  },
  {
    id: "gst_07",
    weddingId: "wed_01HZX0000000000000000002",
    fullName: "Familia Torres Medina",
    phone: "+52 33 4455 6677",
    side: "partner2",
    group: "Family",
    partySize: 5,
    rsvpStatus: "confirmed",
    dietaryNotes: "Two children's plates",
    createdAt: "2025-12-02T09:00:00.000Z",
    invitationStatus: "sent",
    invitationChannel: "phone",
    invitedAt: "2025-12-16T09:00:00.000Z",
    lastContactedAt: "2025-12-16T09:00:00.000Z",
    respondedAt: "2025-12-20T09:00:00.000Z",
    attendingCount: 5,
  },
];

export const DUMMY_BUDGET_ITEMS: BudgetItem[] = [
  {
    id: "bud_01",
    weddingId: "wed_01HZX0000000000000000001",
    category: "venue",
    vendorName: "Hacienda Los Laureles",
    estimatedAmount: 280000,
    actualAmount: 280000,
    paidAmount: 140000,
    createdAt: "2026-02-10T09:00:00.000Z",
  },
  {
    id: "bud_02",
    weddingId: "wed_01HZX0000000000000000001",
    category: "catering",
    estimatedAmount: 220000,
    createdAt: "2026-02-10T09:05:00.000Z",
  },
  {
    id: "bud_03",
    weddingId: "wed_01HZX0000000000000000001",
    category: "photography_video",
    vendorName: "Estudio Lumen",
    estimatedAmount: 65000,
    actualAmount: 68000,
    paidAmount: 68000,
    createdAt: "2026-02-11T09:00:00.000Z",
  },
  {
    id: "bud_04",
    weddingId: "wed_01HZX0000000000000000001",
    category: "flowers_decor",
    estimatedAmount: 45000,
    createdAt: "2026-02-11T09:10:00.000Z",
  },
  {
    id: "bud_05",
    weddingId: "wed_01HZX0000000000000000001",
    category: "attire_beauty",
    estimatedAmount: 38000,
    actualAmount: 35000,
    paidAmount: 35000,
    createdAt: "2026-02-12T09:00:00.000Z",
  },
  {
    id: "bud_06",
    weddingId: "wed_01HZX0000000000000000001",
    category: "music_entertainment",
    vendorName: "Banda Jalisco Nights",
    estimatedAmount: 40000,
    actualAmount: 40000,
    paidAmount: 20000,
    createdAt: "2026-02-12T09:20:00.000Z",
  },
  {
    id: "bud_07",
    weddingId: "wed_01HZX0000000000000000001",
    category: "stationery",
    estimatedAmount: 12000,
    createdAt: "2026-02-13T09:00:00.000Z",
  },
  {
    id: "bud_08",
    weddingId: "wed_01HZX0000000000000000001",
    category: "transportation",
    estimatedAmount: 15000,
    createdAt: "2026-02-13T09:10:00.000Z",
  },
  {
    id: "bud_09",
    weddingId: "wed_01HZX0000000000000000001",
    category: "favors_gifts",
    estimatedAmount: 10000,
    createdAt: "2026-02-13T09:20:00.000Z",
  },
  {
    id: "bud_10",
    weddingId: "wed_01HZX0000000000000000001",
    category: "planning_fees",
    vendorName: "Rivera & Co. Weddings",
    estimatedAmount: 65000,
    actualAmount: 65000,
    paidAmount: 32500,
    notes: "Retainer + day-of coordination fee",
    createdAt: "2026-02-01T09:00:00.000Z",
  },
  {
    id: "bud_11",
    weddingId: "wed_01HZX0000000000000000002",
    category: "venue",
    vendorName: "Casa Luna Jardín",
    estimatedAmount: 200000,
    actualAmount: 205000,
    paidAmount: 205000,
    createdAt: "2025-12-05T09:00:00.000Z",
  },
  {
    id: "bud_12",
    weddingId: "wed_01HZX0000000000000000002",
    category: "catering",
    estimatedAmount: 150000,
    actualAmount: 148000,
    paidAmount: 74000,
    createdAt: "2025-12-06T09:00:00.000Z",
  },
];

export const DUMMY_CHECKLIST_ITEMS: ChecklistItem[] = [
  // Sofía & Marco — wedding 2026-10-17, currently ~3 months out.
  {
    id: "chk_01",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Set the overall budget",
    phase: "twelve_plus_months",
    assigneeRole: "couple",
    isDone: true,
    completedAt: "2026-02-05T09:00:00.000Z",
    createdAt: "2026-02-01T09:00:00.000Z",
  },
  {
    id: "chk_02",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Book the venue and confirm the date",
    phase: "twelve_plus_months",
    assigneeRole: "planner",
    isDone: true,
    completedAt: "2026-02-10T09:00:00.000Z",
    createdAt: "2026-02-01T09:00:00.000Z",
  },
  {
    id: "chk_03",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Book photographer and videographer",
    phase: "nine_months",
    assigneeRole: "planner",
    assigneeName: "Estudio Lumen",
    isDone: true,
    completedAt: "2026-03-15T09:00:00.000Z",
    createdAt: "2026-02-20T09:00:00.000Z",
  },
  {
    id: "chk_04",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Order invitations and stationery",
    phase: "six_months",
    assigneeRole: "planner",
    isDone: true,
    completedAt: "2026-04-20T09:00:00.000Z",
    createdAt: "2026-04-01T09:00:00.000Z",
  },
  {
    id: "chk_05",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Book catering",
    phase: "six_months",
    assigneeRole: "planner",
    isDone: false,
    dueDate: "2026-08-01",
    notes: "Waiting on final quote from two caterers",
    createdAt: "2026-04-01T09:00:00.000Z",
  },
  {
    id: "chk_06",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Send invitations",
    phase: "three_months",
    assigneeRole: "couple",
    isDone: true,
    completedAt: "2026-07-05T09:00:00.000Z",
    createdAt: "2026-06-01T09:00:00.000Z",
  },
  {
    id: "chk_07",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Finalize menu and cake tasting",
    phase: "three_months",
    assigneeRole: "vendor",
    dueDate: "2026-08-10",
    isDone: false,
    createdAt: "2026-06-01T09:00:00.000Z",
  },
  {
    id: "chk_08",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Book hair and makeup trial",
    phase: "three_months",
    assigneeRole: "vendor",
    dueDate: "2026-08-20",
    isDone: false,
    createdAt: "2026-06-05T09:00:00.000Z",
  },
  {
    id: "chk_09",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Confirm final guest count",
    phase: "one_month",
    assigneeRole: "planner",
    dueDate: "2026-09-17",
    isDone: false,
    createdAt: "2026-06-10T09:00:00.000Z",
  },
  {
    id: "chk_10",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Finalize the seating chart",
    phase: "one_month",
    assigneeRole: "planner",
    dueDate: "2026-09-25",
    isDone: false,
    createdAt: "2026-06-10T09:00:00.000Z",
  },
  {
    id: "chk_11",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Confirm timeline with every vendor",
    phase: "week_of",
    assigneeRole: "planner",
    isDone: false,
    createdAt: "2026-06-15T09:00:00.000Z",
  },
  {
    id: "chk_12",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Delegate day-of coordination",
    phase: "day_of",
    assigneeRole: "planner",
    isDone: false,
    createdAt: "2026-06-15T09:00:00.000Z",
  },

  // Elena & Daniel — wedding 2026-08-22, currently in the final month.
  {
    id: "chk_13",
    weddingId: "wed_01HZX0000000000000000002",
    title: "Book the venue and confirm the date",
    phase: "twelve_plus_months",
    assigneeRole: "planner",
    isDone: true,
    completedAt: "2025-12-10T09:00:00.000Z",
    createdAt: "2025-12-01T09:00:00.000Z",
  },
  {
    id: "chk_14",
    weddingId: "wed_01HZX0000000000000000002",
    title: "Send invitations",
    phase: "three_months",
    assigneeRole: "couple",
    isDone: true,
    completedAt: "2026-05-20T09:00:00.000Z",
    createdAt: "2026-05-01T09:00:00.000Z",
  },
  {
    id: "chk_15",
    weddingId: "wed_01HZX0000000000000000002",
    title: "Confirm final guest count",
    phase: "one_month",
    assigneeRole: "planner",
    isDone: true,
    completedAt: "2026-07-15T09:00:00.000Z",
    createdAt: "2026-06-20T09:00:00.000Z",
  },
  {
    id: "chk_16",
    weddingId: "wed_01HZX0000000000000000002",
    title: "Send final payments to vendors",
    phase: "one_month",
    assigneeRole: "couple",
    dueDate: "2026-08-01",
    isDone: false,
    createdAt: "2026-06-20T09:00:00.000Z",
  },
  {
    id: "chk_17",
    weddingId: "wed_01HZX0000000000000000002",
    title: "Confirm timeline with every vendor",
    phase: "week_of",
    assigneeRole: "planner",
    isDone: false,
    createdAt: "2026-06-25T09:00:00.000Z",
  },
  {
    id: "chk_18",
    weddingId: "wed_01HZX0000000000000000002",
    title: "Delegate day-of coordination",
    phase: "day_of",
    assigneeRole: "planner",
    isDone: false,
    createdAt: "2026-06-25T09:00:00.000Z",
  },
];

export const DUMMY_TIMELINE_MILESTONES: TimelineMilestone[] = [
  // Sofía & Marco — wedding 2026-10-17.
  {
    id: "tml_01",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Set the wedding budget",
    dueDate: "2026-02-01",
    ownerRole: "couple",
    isDone: true,
    completedAt: "2026-02-05T09:00:00.000Z",
    createdAt: "2026-01-20T09:00:00.000Z",
  },
  {
    id: "tml_02",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Book the venue",
    dueDate: "2026-02-15",
    ownerRole: "planner",
    isDone: true,
    completedAt: "2026-02-12T09:00:00.000Z",
    createdAt: "2026-01-20T09:00:00.000Z",
  },
  {
    id: "tml_03",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Book key vendors (photo, catering, music)",
    dueDate: "2026-05-01",
    ownerRole: "planner",
    isDone: true,
    completedAt: "2026-04-25T09:00:00.000Z",
    createdAt: "2026-02-01T09:00:00.000Z",
  },
  {
    id: "tml_04",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Order invitations",
    dueDate: "2026-07-01",
    ownerRole: "planner",
    isDone: true,
    completedAt: "2026-06-28T09:00:00.000Z",
    createdAt: "2026-04-01T09:00:00.000Z",
  },
  {
    id: "tml_05",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Send invitations",
    description: "All guest addresses confirmed before mailing.",
    dueDate: "2026-07-20",
    ownerRole: "couple",
    isDone: false,
    createdAt: "2026-04-01T09:00:00.000Z",
  },
  {
    id: "tml_06",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Finalize seating chart",
    dueDate: "2026-08-05",
    ownerRole: "planner",
    isDone: false,
    createdAt: "2026-06-01T09:00:00.000Z",
  },
  {
    id: "tml_07",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Final dress and attire fitting",
    dueDate: "2026-08-15",
    ownerRole: "vendor",
    ownerName: "Atelier Novia",
    isDone: false,
    createdAt: "2026-06-01T09:00:00.000Z",
  },
  {
    id: "tml_08",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Confirm final headcount",
    dueDate: "2026-09-17",
    ownerRole: "planner",
    isDone: false,
    createdAt: "2026-06-10T09:00:00.000Z",
  },
  {
    id: "tml_09",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Final vendor payments due",
    dueDate: "2026-09-20",
    ownerRole: "couple",
    isDone: false,
    createdAt: "2026-06-10T09:00:00.000Z",
  },
  {
    id: "tml_10",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Rehearsal",
    dueDate: "2026-10-16",
    ownerRole: "planner",
    isDone: false,
    createdAt: "2026-06-15T09:00:00.000Z",
  },
  {
    id: "tml_11",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Wedding day",
    dueDate: "2026-10-17",
    ownerRole: "planner",
    isDone: false,
    createdAt: "2026-06-15T09:00:00.000Z",
  },

  // Elena & Daniel — wedding 2026-08-22.
  {
    id: "tml_12",
    weddingId: "wed_01HZX0000000000000000002",
    title: "Set the wedding budget",
    dueDate: "2025-12-15",
    ownerRole: "couple",
    isDone: true,
    completedAt: "2025-12-14T09:00:00.000Z",
    createdAt: "2025-12-01T09:00:00.000Z",
  },
  {
    id: "tml_13",
    weddingId: "wed_01HZX0000000000000000002",
    title: "Book the venue",
    dueDate: "2025-12-20",
    ownerRole: "planner",
    isDone: true,
    completedAt: "2025-12-18T09:00:00.000Z",
    createdAt: "2025-12-01T09:00:00.000Z",
  },
  {
    id: "tml_14",
    weddingId: "wed_01HZX0000000000000000002",
    title: "Send invitations",
    dueDate: "2026-05-20",
    ownerRole: "couple",
    isDone: true,
    completedAt: "2026-05-18T09:00:00.000Z",
    createdAt: "2026-05-01T09:00:00.000Z",
  },
  {
    id: "tml_15",
    weddingId: "wed_01HZX0000000000000000002",
    title: "Confirm final headcount",
    dueDate: "2026-07-15",
    ownerRole: "planner",
    isDone: true,
    completedAt: "2026-07-15T09:00:00.000Z",
    createdAt: "2026-06-20T09:00:00.000Z",
  },
  {
    id: "tml_16",
    weddingId: "wed_01HZX0000000000000000002",
    title: "Final vendor payments due",
    dueDate: "2026-08-01",
    ownerRole: "couple",
    isDone: false,
    createdAt: "2026-06-20T09:00:00.000Z",
  },
  {
    id: "tml_17",
    weddingId: "wed_01HZX0000000000000000002",
    title: "Rehearsal",
    dueDate: "2026-08-21",
    ownerRole: "planner",
    isDone: false,
    createdAt: "2026-06-25T09:00:00.000Z",
  },
  {
    id: "tml_18",
    weddingId: "wed_01HZX0000000000000000002",
    title: "Wedding day",
    dueDate: "2026-08-22",
    ownerRole: "planner",
    isDone: false,
    createdAt: "2026-06-25T09:00:00.000Z",
  },
];

export const DUMMY_VENDORS: Vendor[] = [
  {
    id: "vnd_01",
    organizationId: "org_01HZX0000000000000000001",
    businessName: "Hacienda Los Laureles",
    category: "venue",
    phone: "+52 33 1111 2222",
    website: "https://haciendaloslaureles.example",
    rating: 5,
    createdAt: "2026-01-25T09:00:00.000Z",
  },
  {
    id: "vnd_02",
    organizationId: "org_01HZX0000000000000000001",
    businessName: "Casa Luna Jardín",
    category: "venue",
    phone: "+52 33 2222 3333",
    rating: 4,
    createdAt: "2025-11-15T09:00:00.000Z",
  },
  {
    id: "vnd_03",
    organizationId: "org_01HZX0000000000000000001",
    businessName: "Estudio Lumen",
    category: "photography_video",
    contactName: "Renata Campos",
    email: "hola@estudiolumen.example",
    instagram: "@estudiolumen",
    rating: 5,
    createdAt: "2026-02-05T09:00:00.000Z",
  },
  {
    id: "vnd_04",
    organizationId: "org_01HZX0000000000000000001",
    businessName: "Banda Jalisco Nights",
    category: "music_entertainment",
    phone: "+52 33 4444 5555",
    rating: 4,
    createdAt: "2026-02-10T09:00:00.000Z",
  },
  {
    id: "vnd_05",
    organizationId: "org_01HZX0000000000000000001",
    businessName: "Atelier Novia",
    category: "attire_beauty",
    email: "citas@ateliernovia.example",
    instagram: "@ateliernovia",
    rating: 5,
    createdAt: "2026-02-15T09:00:00.000Z",
  },
  {
    id: "vnd_06",
    organizationId: "org_01HZX0000000000000000001",
    businessName: "Sabores de Jalisco",
    category: "catering",
    contactName: "Miguel Ángel Torres",
    phone: "+52 33 6666 7777",
    rating: 4,
    notes: "Strong regional menu, needs 2-week lead time for tastings.",
    createdAt: "2026-03-01T09:00:00.000Z",
  },
  {
    id: "vnd_07",
    organizationId: "org_01HZX0000000000000000001",
    businessName: "Flora & Co.",
    category: "flowers_decor",
    email: "pedidos@floraco.example",
    createdAt: "2026-03-10T09:00:00.000Z",
  },
];

export const DUMMY_VENDOR_BOOKINGS: VendorBooking[] = [
  {
    id: "vbk_01",
    organizationId: "org_01HZX0000000000000000001",
    weddingId: "wed_01HZX0000000000000000001",
    vendorId: "vnd_01",
    status: "booked",
    contractedAmount: 280000,
    createdAt: "2026-02-10T09:00:00.000Z",
  },
  {
    id: "vbk_02",
    organizationId: "org_01HZX0000000000000000001",
    weddingId: "wed_01HZX0000000000000000001",
    vendorId: "vnd_03",
    status: "booked",
    contractedAmount: 68000,
    createdAt: "2026-03-15T09:00:00.000Z",
  },
  {
    id: "vbk_03",
    organizationId: "org_01HZX0000000000000000001",
    weddingId: "wed_01HZX0000000000000000001",
    vendorId: "vnd_04",
    status: "booked",
    contractedAmount: 40000,
    createdAt: "2026-03-20T09:00:00.000Z",
  },
  {
    id: "vbk_04",
    organizationId: "org_01HZX0000000000000000001",
    weddingId: "wed_01HZX0000000000000000001",
    vendorId: "vnd_05",
    status: "booked",
    contractedAmount: 35000,
    createdAt: "2026-03-22T09:00:00.000Z",
  },
  {
    id: "vbk_05",
    organizationId: "org_01HZX0000000000000000001",
    weddingId: "wed_01HZX0000000000000000001",
    vendorId: "vnd_06",
    status: "quoted",
    quotedAmount: 225000,
    notes: "Waiting on final headcount before confirming.",
    createdAt: "2026-06-01T09:00:00.000Z",
  },
  {
    id: "vbk_06",
    organizationId: "org_01HZX0000000000000000001",
    weddingId: "wed_01HZX0000000000000000001",
    vendorId: "vnd_07",
    status: "considered",
    createdAt: "2026-06-05T09:00:00.000Z",
  },
  {
    id: "vbk_07",
    organizationId: "org_01HZX0000000000000000001",
    weddingId: "wed_01HZX0000000000000000002",
    vendorId: "vnd_02",
    status: "booked",
    contractedAmount: 205000,
    createdAt: "2025-12-05T09:00:00.000Z",
  },
  {
    id: "vbk_08",
    organizationId: "org_01HZX0000000000000000001",
    weddingId: "wed_01HZX0000000000000000002",
    vendorId: "vnd_06",
    status: "booked",
    contractedAmount: 148000,
    createdAt: "2025-12-10T09:00:00.000Z",
  },
];

export const DUMMY_RUN_OF_SHOW_ITEMS: RunOfShowItem[] = [
  // Sofía & Marco — wedding day 2026-10-17.
  {
    id: "ros_01",
    weddingId: "wed_01HZX0000000000000000001",
    startTime: "11:30",
    durationMinutes: 30,
    title: "Vendor final payment reminder",
    area: "Logistics",
    isClientVisible: false,
    notes: "Confirm cash envelopes with coordinator before hair & makeup starts.",
    createdAt: "2026-06-20T09:00:00.000Z",
  },
  {
    id: "ros_02",
    weddingId: "wed_01HZX0000000000000000001",
    startTime: "12:00",
    durationMinutes: 180,
    title: "Hair & makeup",
    area: "Getting Ready",
    responsibleName: "Atelier Novia",
    isClientVisible: true,
    createdAt: "2026-06-20T09:00:00.000Z",
  },
  {
    id: "ros_03",
    weddingId: "wed_01HZX0000000000000000001",
    startTime: "15:00",
    durationMinutes: 60,
    title: "First look & wedding party photos",
    area: "Getting Ready",
    responsibleName: "Estudio Lumen",
    isClientVisible: true,
    createdAt: "2026-06-20T09:00:00.000Z",
  },
  {
    id: "ros_04",
    weddingId: "wed_01HZX0000000000000000001",
    startTime: "16:30",
    durationMinutes: 30,
    title: "Ceremony",
    area: "Ceremony",
    location: "Garden pavilion",
    isClientVisible: true,
    createdAt: "2026-06-20T09:00:00.000Z",
  },
  {
    id: "ros_05",
    weddingId: "wed_01HZX0000000000000000001",
    startTime: "17:00",
    durationMinutes: 60,
    title: "Cocktail hour",
    area: "Cocktail Hour",
    location: "Terrace",
    isClientVisible: true,
    createdAt: "2026-06-20T09:00:00.000Z",
  },
  {
    id: "ros_06",
    weddingId: "wed_01HZX0000000000000000001",
    startTime: "18:00",
    durationMinutes: 15,
    title: "Grand entrance",
    area: "Reception",
    isClientVisible: true,
    createdAt: "2026-06-20T09:00:00.000Z",
  },
  {
    id: "ros_07",
    weddingId: "wed_01HZX0000000000000000001",
    startTime: "18:15",
    durationMinutes: 10,
    title: "First dance",
    area: "Reception",
    isClientVisible: true,
    createdAt: "2026-06-20T09:00:00.000Z",
  },
  {
    id: "ros_08",
    weddingId: "wed_01HZX0000000000000000001",
    startTime: "18:30",
    durationMinutes: 60,
    title: "Dinner service",
    area: "Reception",
    responsibleName: "Sabores de Jalisco",
    isClientVisible: true,
    createdAt: "2026-06-20T09:00:00.000Z",
  },
  {
    id: "ros_09",
    weddingId: "wed_01HZX0000000000000000001",
    startTime: "19:30",
    durationMinutes: 20,
    title: "Toasts",
    area: "Reception",
    isClientVisible: true,
    createdAt: "2026-06-20T09:00:00.000Z",
  },
  {
    id: "ros_10",
    weddingId: "wed_01HZX0000000000000000001",
    startTime: "19:50",
    durationMinutes: 15,
    title: "Cake cutting",
    area: "Reception",
    isClientVisible: true,
    createdAt: "2026-06-20T09:00:00.000Z",
  },
  {
    id: "ros_11",
    weddingId: "wed_01HZX0000000000000000001",
    startTime: "20:05",
    durationMinutes: 120,
    title: "Open dancing",
    area: "Reception",
    responsibleName: "Banda Jalisco Nights",
    isClientVisible: true,
    createdAt: "2026-06-20T09:00:00.000Z",
  },
  {
    id: "ros_12",
    weddingId: "wed_01HZX0000000000000000001",
    startTime: "23:45",
    durationMinutes: 15,
    title: "Grand send-off",
    area: "Send-off",
    isClientVisible: true,
    createdAt: "2026-06-20T09:00:00.000Z",
  },

  // Elena & Daniel — wedding day 2026-08-22.
  {
    id: "ros_13",
    weddingId: "wed_01HZX0000000000000000002",
    startTime: "13:00",
    durationMinutes: 120,
    title: "Hair & makeup",
    area: "Getting Ready",
    isClientVisible: true,
    createdAt: "2026-06-25T09:00:00.000Z",
  },
  {
    id: "ros_14",
    weddingId: "wed_01HZX0000000000000000002",
    startTime: "16:00",
    durationMinutes: 30,
    title: "Ceremony",
    area: "Ceremony",
    location: "Casa Luna Jardín — main garden",
    isClientVisible: true,
    createdAt: "2026-06-25T09:00:00.000Z",
  },
  {
    id: "ros_15",
    weddingId: "wed_01HZX0000000000000000002",
    startTime: "16:30",
    durationMinutes: 60,
    title: "Cocktail hour",
    area: "Cocktail Hour",
    isClientVisible: true,
    createdAt: "2026-06-25T09:00:00.000Z",
  },
  {
    id: "ros_16",
    weddingId: "wed_01HZX0000000000000000002",
    startTime: "17:30",
    durationMinutes: 60,
    title: "Dinner service",
    area: "Reception",
    responsibleName: "Sabores de Jalisco",
    isClientVisible: true,
    createdAt: "2026-06-25T09:00:00.000Z",
  },
  {
    id: "ros_17",
    weddingId: "wed_01HZX0000000000000000002",
    startTime: "18:30",
    durationMinutes: 150,
    title: "Open dancing",
    area: "Reception",
    isClientVisible: true,
    createdAt: "2026-06-25T09:00:00.000Z",
  },
  {
    id: "ros_18",
    weddingId: "wed_01HZX0000000000000000002",
    startTime: "21:15",
    durationMinutes: 15,
    title: "Grand send-off",
    area: "Send-off",
    isClientVisible: true,
    createdAt: "2026-06-25T09:00:00.000Z",
  },
];

export const DUMMY_CONTRACTS: Contract[] = [
  // Sofía & Marco — wedding 2026-10-17.
  {
    id: "ctr_01",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Venue rental agreement",
    contractType: "Venue rental",
    partyType: "vendor",
    vendorId: "vnd_01",
    status: "signed",
    totalAmount: 280000,
    sentAt: "2026-02-08T09:00:00.000Z",
    signedAt: "2026-02-12T09:00:00.000Z",
    effectiveDate: "2026-02-12",
    expirationDate: "2026-10-18",
    createdAt: "2026-02-05T09:00:00.000Z",
  },
  {
    id: "ctr_02",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Photography & video services",
    contractType: "Photography & video",
    partyType: "vendor",
    vendorId: "vnd_03",
    status: "signed",
    totalAmount: 68000,
    sentAt: "2026-03-16T09:00:00.000Z",
    signedAt: "2026-03-20T09:00:00.000Z",
    effectiveDate: "2026-03-20",
    createdAt: "2026-03-15T09:00:00.000Z",
  },
  {
    id: "ctr_03",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Music & entertainment agreement",
    contractType: "Music & entertainment",
    partyType: "vendor",
    vendorId: "vnd_04",
    status: "signed",
    totalAmount: 40000,
    sentAt: "2026-03-21T09:00:00.000Z",
    signedAt: "2026-03-25T09:00:00.000Z",
    effectiveDate: "2026-03-25",
    createdAt: "2026-03-20T09:00:00.000Z",
  },
  {
    id: "ctr_04",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Bridal attire agreement",
    contractType: "Rentals & décor",
    partyType: "vendor",
    vendorId: "vnd_05",
    status: "signed",
    totalAmount: 35000,
    sentAt: "2026-03-28T09:00:00.000Z",
    signedAt: "2026-04-01T09:00:00.000Z",
    effectiveDate: "2026-04-01",
    createdAt: "2026-03-25T09:00:00.000Z",
  },
  {
    id: "ctr_05",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Catering agreement",
    contractType: "Catering agreement",
    partyType: "vendor",
    vendorId: "vnd_06",
    status: "under_review",
    totalAmount: 225000,
    sentAt: "2026-06-05T09:00:00.000Z",
    notes: "Waiting on their redline before Sofía & Marco sign.",
    createdAt: "2026-06-01T09:00:00.000Z",
  },
  {
    id: "ctr_06",
    weddingId: "wed_01HZX0000000000000000001",
    title: "Planning & coordination services",
    contractType: "Planning & coordination services",
    partyType: "client",
    clientName: "Sofía & Marco",
    status: "signed",
    totalAmount: 65000,
    sentAt: "2026-01-28T09:00:00.000Z",
    signedAt: "2026-02-01T09:00:00.000Z",
    effectiveDate: "2026-02-01",
    createdAt: "2026-01-25T09:00:00.000Z",
  },

  // Elena & Daniel — wedding 2026-08-22.
  {
    id: "ctr_07",
    weddingId: "wed_01HZX0000000000000000002",
    title: "Venue rental agreement",
    contractType: "Venue rental",
    partyType: "vendor",
    vendorId: "vnd_02",
    status: "signed",
    totalAmount: 205000,
    sentAt: "2025-12-14T09:00:00.000Z",
    signedAt: "2025-12-18T09:00:00.000Z",
    effectiveDate: "2025-12-18",
    expirationDate: "2026-08-23",
    createdAt: "2025-12-10T09:00:00.000Z",
  },
  {
    id: "ctr_08",
    weddingId: "wed_01HZX0000000000000000002",
    title: "Catering agreement",
    contractType: "Catering agreement",
    partyType: "vendor",
    vendorId: "vnd_06",
    status: "signed",
    totalAmount: 148000,
    sentAt: "2025-12-16T09:00:00.000Z",
    signedAt: "2025-12-20T09:00:00.000Z",
    effectiveDate: "2025-12-20",
    createdAt: "2025-12-12T09:00:00.000Z",
  },
];

export const DUMMY_PALETTE_COLORS: PaletteColor[] = [
  // Sofía & Marco — warm terracotta hacienda palette.
  { id: "pal_01", weddingId: "wed_01HZX0000000000000000001", hex: "#C1653D", label: "Terracotta", sortOrder: 0, createdAt: "2026-03-01T09:00:00.000Z" },
  { id: "pal_02", weddingId: "wed_01HZX0000000000000000001", hex: "#8A9A7B", label: "Sage green", sortOrder: 1, createdAt: "2026-03-01T09:00:00.000Z" },
  { id: "pal_03", weddingId: "wed_01HZX0000000000000000001", hex: "#F5EDE1", label: "Ivory", sortOrder: 2, createdAt: "2026-03-01T09:00:00.000Z" },
  { id: "pal_04", weddingId: "wed_01HZX0000000000000000001", hex: "#C9A24B", label: "Warm gold", sortOrder: 3, createdAt: "2026-03-01T09:00:00.000Z" },

  // Elena & Daniel — dusty rose garden palette.
  { id: "pal_05", weddingId: "wed_01HZX0000000000000000002", hex: "#C98A94", label: "Dusty rose", sortOrder: 0, createdAt: "2025-12-15T09:00:00.000Z" },
  { id: "pal_06", weddingId: "wed_01HZX0000000000000000002", hex: "#7A8B7F", label: "Eucalyptus", sortOrder: 1, createdAt: "2025-12-15T09:00:00.000Z" },
  { id: "pal_07", weddingId: "wed_01HZX0000000000000000002", hex: "#F7F1E8", label: "Cream", sortOrder: 2, createdAt: "2025-12-15T09:00:00.000Z" },
];

export const DUMMY_MOODBOARD_ITEMS: MoodboardItem[] = [
  {
    id: "mbd_01",
    weddingId: "wed_01HZX0000000000000000001",
    imageUrl: "/images/editorial/hacienda-banquet.jpg",
    title: "Hacienda banquet at blue hour",
    category: "venue",
    isFavorite: true,
    createdAt: "2026-03-05T09:00:00.000Z",
  },
  {
    id: "mbd_02",
    weddingId: "wed_01HZX0000000000000000001",
    imageUrl: "/images/editorial/floral-detail.jpg",
    title: "Bordeaux, ivory & terracotta florals",
    category: "florals",
    isFavorite: true,
    createdAt: "2026-03-06T09:00:00.000Z",
  },
  {
    id: "mbd_03",
    weddingId: "wed_01HZX0000000000000000001",
    imageUrl: "/images/editorial/dance-floor.jpg",
    title: "Warm hacienda dance floor",
    category: "decor",
    notes: "Love the candle height — maybe fewer eucalyptus sprigs down the runner.",
    isFavorite: false,
    createdAt: "2026-03-10T09:00:00.000Z",
  },
  {
    id: "mbd_04",
    weddingId: "wed_01HZX0000000000000000001",
    imageUrl: "https://picsum.photos/seed/naked-cake/640/480",
    title: "Three-tier naked cake with florals",
    category: "cake",
    isFavorite: true,
    createdAt: "2026-03-12T09:00:00.000Z",
  },
  {
    id: "mbd_05",
    weddingId: "wed_01HZX0000000000000000001",
    imageUrl: "https://picsum.photos/seed/lace-gown/640/480",
    title: "Flowing lace gown silhouette",
    category: "attire",
    isFavorite: false,
    createdAt: "2026-03-15T09:00:00.000Z",
  },
  {
    id: "mbd_06",
    weddingId: "wed_01HZX0000000000000000001",
    imageUrl: "https://picsum.photos/seed/invitation-suite/640/480",
    title: "Hand-lettered invitation suite",
    category: "stationery",
    isFavorite: true,
    createdAt: "2026-03-18T09:00:00.000Z",
  },

  // Elena & Daniel
  {
    id: "mbd_07",
    weddingId: "wed_01HZX0000000000000000002",
    imageUrl: "https://picsum.photos/seed/garden-ceremony/640/480",
    title: "Garden ceremony arch",
    category: "decor",
    isFavorite: true,
    createdAt: "2025-12-20T09:00:00.000Z",
  },
  {
    id: "mbd_08",
    weddingId: "wed_01HZX0000000000000000002",
    imageUrl: "https://picsum.photos/seed/rose-bouquet/640/480",
    title: "Dusty rose & eucalyptus bouquet",
    category: "florals",
    isFavorite: true,
    createdAt: "2025-12-22T09:00:00.000Z",
  },
  {
    id: "mbd_09",
    weddingId: "wed_01HZX0000000000000000002",
    imageUrl: "https://picsum.photos/seed/romantic-updo/640/480",
    title: "Soft romantic updo",
    category: "hair_makeup",
    isFavorite: false,
    createdAt: "2026-01-05T09:00:00.000Z",
  },
];
